import { ChangeEvent, FC, MouseEvent, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

import {
  DropdownWrapperMedium, InputWrapperLarge, InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Textarea from '../../../components/Textarea/Textarea';
import { useUpdateBase } from '../../../services/base/base.service';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';
import { EditForm, EditFormActions, EditFormFields, FormFieldRow, FormRadioGroupContainer } from "../EditForm.styles";
import { Base } from "../../../services/base/base.types";
import { statusOptions } from "./StatusOptions";

interface EditBaseFormProps {
  base: Base;
  onSuccess: () => void;
}

interface EditBaseFormValues {
  status: string,
  synopsis: string,
  displayDescription: string,
  sequence: string,
  displayInOrderEntry: boolean,
  validOnWeb: boolean,
}

const EditBaseForm: FC<EditBaseFormProps> = (
    {
      base,
      onSuccess
    }
) => {
  const baseId = base.baseNo;
  const {
    mutate: updateBase,
    isLoading: updateBaseIsLoading,
    isError: updateBaseIsError,
    error: updateBaseError
  } = useUpdateBase({ baseId });

  useGenericErrorMessage([
    {
      isError: updateBaseIsError,
      error: updateBaseError
    }
  ]);

  const initialFormValues = useMemo<EditBaseFormValues>(() => ({
    status: base.statusNo?.toString() ?? '',
    displayDescription: base.displayDesc,
    synopsis: base.synopsis,
    sequence: base.seqNo?.toString() ?? '',
    displayInOrderEntry: base.displayInOe,
    validOnWeb: base.validOnWeb
  }), [base]);

  const [formValues, setFormValues] = useState<EditBaseFormValues>(initialFormValues);

  const [formErrors, setFormErrors] = useState({
    displayDescription: '',
    sequence: '',
    synopsis: '',
    status: ''
  });

  const unsavedChanges = !isEqual(initialFormValues, formValues);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const updateFormValuesFromRadioGroup = (
      event: ChangeEvent<HTMLInputElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value.includes('Yes')
    });
  }

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      displayDescription: '',
      sequence: '',
      synopsis: '',
      status: ''
    };

    if (formValues.displayDescription?.length > 9) {
      error = true;
      newFormErrors.displayDescription = 'BOH Description must be 9 characters or less';
    }
    if (!formValues.displayDescription || formValues.displayDescription.length < 1) {
      error = true;
      newFormErrors.displayDescription = 'Please enter a BOH Description';
    }
    if ([null, undefined, -1, ''].includes(formValues.sequence)) {
      error = true;
      newFormErrors.sequence = 'Sequence Number is required.';
    }
    if (formValues.sequence?.length > 3) {
      error = true;
      newFormErrors.sequence = 'Sequence Number must be 3 characters or less'
    }
    if (formValues.synopsis?.length > 255) {
      error = true;
      newFormErrors.synopsis = 'Synopsis must be 255 characters or less';
    }
    if (error) {
      setFormErrors(newFormErrors);
      return;
    }

    updateBase(
        {
          status: +formValues.status,
          displayDescription: formValues.displayDescription,
          synopsis: formValues.synopsis,
          sequence: +formValues.sequence,
          displayInOrderEntry: formValues.displayInOrderEntry,
          validOnWeb: formValues.validOnWeb
        },
        {
          onSuccess: onSuccess
        }
    );
  };

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Base Number"}
                  id={'baseNumber'}
                  value={base.baseNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Short Description"}
                  id={'description'}
                  value={base.description}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={'longDescription'}
                  value={base.longDescription}
                  disabled
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <Textarea
                label={"Synopsis"}
                id={'synopsis'}
                name={'synopsis'}
                value={formValues.synopsis}
                onChange={updateFormValues}
                errorText={formErrors.synopsis}
            />
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"BOH Description"}
                  id={'displayDescription'}
                  name={'displayDescription'}
                  value={formValues.displayDescription}
                  onChange={updateFormValues}
                  errorText={formErrors.displayDescription}
              />
            </InputWrapperLarge>
            <DropdownWrapperMedium>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status}
                  onChange={updateFormValues}
                  errorText={formErrors?.status}
              />
            </DropdownWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Mnemonic"}
                  id={'mnemonic'}
                  value={base.mnemonic}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Sequence Number"}
                  id={'sequenceNumber'}
                  name={'sequence'}
                  type={'number'}
                  value={formValues.sequence}
                  onChange={updateFormValues}
                  errorText={formErrors?.sequence}
              />
            </InputWrapperMedium>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid on Web?"}
                  id={'validOnWeb'}
                  options={[
                    {
                      id: 'validOnWebYes',
                      label: 'Yes'
                    },
                    {
                      id: 'validOnWebNo',
                      label: 'No'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid in Order Entry?"}
                  id={'displayInOrderEntry'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'displayInOrderEntryYes'
                    },
                    {
                      label: 'No',
                      id: 'displayInOrderEntryNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.displayInOrderEntry ? 'displayInOrderEntryYes' : 'displayInOrderEntryNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <SubTitle>{readOnlyLabelTableText}</SubTitle>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSave}
              disabled={!unsavedChanges}
              isLoading={updateBaseIsLoading}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChanges}/>
      </EditForm>
  );
};

export default EditBaseForm;
